<template>
  <v-dialog
    :value="visible"
    max-width="960"
    width="960"
    content-class="premium-dialog"
    @input="onDialogInput"
  >
    <v-card class="pa-0 dialog-card">
      <v-toolbar flat color="white" dense class="pt-2">
        <v-toolbar-title class="subheading font-weight-bold grey--text text--darken-2">
          NOVA SOLICITAÇÃO
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon small @click="close">
          <v-icon color="grey">close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="px-6 pt-2 pb-5 dialog-body">
        <v-container grid-list-md class="pa-0">
          <form v-on:submit.prevent="save">
            <v-layout row wrap class="form-grid">
              <v-flex xs12 class="mb-4 text-xs-center">
                <div class="headline primary--text font-weight-medium">Solicitar Pagamento</div>
                <div class="caption grey--text text--darken-1 mt-1">Preencha os dados e anexe o boleto.</div>
              </v-flex>

              <v-flex xs12>
                <div class="resumo-box">
                  <div class="caption grey--text text--darken-1">Resumo</div>
                  <div class="subheading mt-1"><strong>{{ form.descricao || 'Sem descrição' }}</strong></div>
                  <div class="title secondary--text mt-1">{{ valorPreview }}</div>
                  <div class="caption grey--text text--darken-1 mt-1">
                    Vencimento: {{ form.data_vencimento ? formatDate(form.data_vencimento) : '--' }}
                  </div>
                </div>
              </v-flex>

              <v-flex xs12>
                <v-text-field
                  label="Descrição"
                  placeholder="Ex: Manutenção Elétrica"
                  v-model="form.descricao"
                  v-validate="'required'"
                  name="descricao"
                  data-vv-as="descrição"
                  :error-messages="errors.has('descricao') ? errors.first('descricao') : []"
                  class="premium-input"
                ></v-text-field>
              </v-flex>

              <v-flex xs12>
                <div class="field-label">Valor</div>
                <v-text-field
                  v-model="form.valor"
                  @input="onValorInput"
                  v-validate="'required'"
                  name="valor"
                  data-vv-as="valor"
                  placeholder="Ex.: 1.250,90"
                  single-line
                  hint="Formato: 1.250,90"
                  :persistent-hint="false"
                  :error-messages="errors.has('valor') ? errors.first('valor') : []"
                  class="premium-input"
                ></v-text-field>
              </v-flex>

              <v-flex xs12>
                <div class="field-label">Vencimento</div>
                <v-text-field
                  v-model="form.data_vencimento"
                  type="date"
                  v-validate="'required'"
                  name="data_vencimento"
                  data-vv-as="vencimento"
                  single-line
                  :hint="form.data_vencimento ? `Data selecionada: ${formatDate(form.data_vencimento)}` : 'Formato de exibição: dd/mm/aaaa'"
                  :persistent-hint="false"
                  :error-messages="errors.has('data_vencimento') ? errors.first('data_vencimento') : []"
                  class="premium-input"
                ></v-text-field>
              </v-flex>

              <v-flex xs12>
                <v-select
                  :items="users"
                  item-text="name"
                  item-value="id"
                  label="Aprovador Responsável"
                  v-model="form.aprovador_id"
                  v-validate="'required'"
                  name="aprovador"
                  data-vv-as="aprovador"
                  :error-messages="errors.has('aprovador') ? errors.first('aprovador') : []"
                  class="premium-input"
                ></v-select>
              </v-flex>

              <v-flex xs12>
                <div
                  class="upload-zone text-xs-center pa-4 mt-2"
                  :class="{'has-file': file}"
                  @click="$refs.fileInput.click()"
                >
                  <input type="file" ref="fileInput" @change="onFileChange" accept="image/*,application/pdf" style="display: none;">

                  <template v-if="!file">
                    <v-icon size="32" color="grey lighten-1">cloud_upload</v-icon>
                    <div class="subheading grey--text text--darken-1 mt-2">Anexar Boleto</div>
                    <div class="caption grey--text lighten-1">PDF ou Imagem (Máx 5MB)</div>
                  </template>

                  <template v-else>
                    <v-icon size="32" color="green">check_circle_outline</v-icon>
                    <div class="subheading green--text text--darken-1 mt-2 text-truncate" style="max-width: 100%;">
                      {{ fileName }}
                    </div>
                    <v-btn small flat color="red" @click.stop="resetFile" class="mt-1">Trocar arquivo</v-btn>
                  </template>
                </div>
              </v-flex>
            </v-layout>
          </form>
        </v-container>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-3 dialog-actions">
        <v-btn flat large color="grey" @click="close" class="font-weight-bold btn-cancelar">Cancelar</v-btn>
        <v-spacer></v-spacer>
        <v-btn
          depressed
          large
          color="secondary"
          class="px-4 font-weight-bold btn-confirmar"
          :disabled="isFormInvalid"
          :loading="loading"
          @click="save"
        >
          Solicitar Aprovação
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import { apiURL } from '@/config'
import { formatBRDate, formatBRLCurrency, normalizeMoney } from '@/utils/formatters'

export default {
  props: ['visible', 'imovelId'],
  data () {
    return {
      users: [],
      form: {
        descricao: '',
        valor: '',
        data_vencimento: '',
        aprovador_id: null
      },
      file: null,
      fileName: '',
      loading: false,
      openedAt: 0
    }
  },
  computed: {
    isFormInvalid () {
      const fieldsInvalid = this.fields ? Object.keys(this.fields).some(key => this.fields[key].invalid) : false
      const hasEmptyFields = !this.form.descricao || !this.form.valor || !this.form.data_vencimento || !this.form.aprovador_id
      return fieldsInvalid || hasEmptyFields || !this.file || this.loading
    },
    valorPreview () {
      return this.form.valor ? this.formatCurrency(this.form.valor) : 'R$ 0,00'
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.openedAt = Date.now()
        this.loadUsers()
        this.resetForm()
      }
    }
  },
  methods: {
    isBlockedUser (user) {
      if (!user) return true
      const blocked = user.bloqueado
      return blocked === true || blocked === 1 || blocked === '1'
    },
    normalizeApprovers (users) {
      if (!Array.isArray(users)) return []

      return users
        .filter(user => !this.isBlockedUser(user))
        .sort((a, b) => {
          const nameA = String((a && a.name) || '')
          const nameB = String((b && b.name) || '')
          return nameA.localeCompare(nameB, 'pt-BR')
        })
    },
    loadUsers () {
      const endpoints = ['/pagamentos/aprovadores', '/users', '/usuarios']

      const requestApprovers = (index = 0) => {
        if (index >= endpoints.length) {
          return Promise.reject(new Error('approvers_not_available'))
        }

        return axios.get(`${apiURL}${endpoints[index]}`).then((response) => {
          return response && response.data ? response.data.data : []
        }).catch((error) => {
          const status = error && error.response ? error.response.status : null
          const canFallback = index < endpoints.length - 1 && (status === 404 || status === 405)
          if (canFallback) {
            return requestApprovers(index + 1)
          }
          return Promise.reject(error)
        })
      }

      requestApprovers().then((users) => {
        this.users = this.normalizeApprovers(users)

        if (this.users.length < 1) {
          this.$store.dispatch('utils/showSnackbar', {
            type: 'info',
            text: 'Nenhum usuário ativo disponível para aprovador.'
          })
        }
      }).catch((error) => {
        this.users = []
        const status = error && error.response ? error.response.status : null
        const text = status === 403
          ? 'Sem permissão para carregar aprovadores.'
          : 'Não foi possível carregar a lista de aprovadores.'

        this.$store.dispatch('utils/showSnackbar', {
          type: 'error',
          text
        })
      })
    },
    onValorInput (value) {
      this.form.valor = this.formatCurrencyInput(value)
    },
    formatCurrencyInput (value) {
      const digits = String(value || '').replace(/\D/g, '')
      if (!digits) return ''
      const amount = parseInt(digits, 10) / 100

      return amount.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    normalizeMoney: normalizeMoney,
    formatCurrency: formatBRLCurrency,
    formatDate: formatBRDate,
    onFileChange (e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) return
      this.file = files[0]
      this.fileName = files[0].name
    },
    resetFile () {
      this.file = null
      this.fileName = ''
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    resetForm () {
      this.form = {
        descricao: '',
        valor: '',
        data_vencimento: '',
        aprovador_id: null
      }
      this.file = null
      this.fileName = ''
      this.loading = false
      if (this.$validator) {
        this.$validator.pause()
        this.$nextTick(() => {
          this.$validator.errors.clear()
          this.$validator.resume()
        })
      }
    },
    onDialogInput (value) {
      if (value === false && !this.loading) {
        if (Date.now() - this.openedAt < 200) return
        this.close()
      }
    },
    close () {
      this.$emit('close')
    },
    save () {
      if (this.loading) return

      this.$store.dispatch('utils/showLoading')
      this.loading = true

      this.$validator.validateAll().then(result => {
        if (result && this.file) {
          const formData = new FormData()
          formData.append('imovel_id', this.imovelId)
          formData.append('documento', this.file)

          Object.keys(this.form).forEach(key => {
            let value = this.form[key]
            if (key === 'valor') {
              value = this.normalizeMoney(value).toFixed(2)
            }
            formData.append(key, value)
          })

          axios.post(`${apiURL}/pagamentos`, formData).then(() => {
            this.loading = false
            this.$store.dispatch('utils/hideLoading')
            this.$store.dispatch('utils/showSnackbar', {
              type: 'success',
              text: 'Solicitação de pagamento enviada!'
            })
            this.$emit('saved')
            this.close()
          }).catch(error => {
            this.loading = false
            this.$store.dispatch('utils/hideLoading')
            let text = 'Erro ao enviar solicitação.'
            if (error.response && error.response.data && error.response.data.errors) {
              const errors = error.response.data.errors
              text = Object.keys(errors).map(key => errors[key][0]).join(' | ')
            } else if (error.response && error.response.data && error.response.data.error) {
              text = error.response.data.error
            }
            this.$store.dispatch('utils/showSnackbar', {
              type: 'error',
              text: text
            })
          })
        } else {
          this.loading = false
          this.$store.dispatch('utils/hideLoading')
        }
      })
    }
  }
}
</script>

<style scoped>
.dialog-card {
  width: 100%;
  max-width: none;
}

.dialog-body {
  max-height: 78vh;
  overflow-y: auto;
}

.form-grid .flex {
  margin-bottom: 10px;
}

.field-label {
  color: rgba(0, 0, 0, 0.62);
  font-size: 13px;
  line-height: 1;
  margin-bottom: 4px;
}

.resumo-box {
  border: 1px solid #ebebeb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  background: #fafafa;
  display: none;
}

.upload-zone {
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.upload-zone:hover {
  border-color: #bdbdbd;
  background-color: #f5f5f5;
}
.upload-zone.has-file {
  border-color: #81c784;
  background-color: #f1f8e9;
  border-style: solid;
}

@media (max-width: 599px) {
  .dialog-actions {
    display: block;
  }

  .dialog-actions .spacer {
    display: none;
  }

  .dialog-actions .btn {
    width: 100%;
    margin: 0 0 8px 0 !important;
  }

  .dialog-actions .btn:last-child {
    margin-bottom: 0 !important;
  }
}
</style>



// WEBPACK FOOTER //
// PagamentosAdicionar.vue?65874d04